import {
  TextInputV2,
  Select,
  TextV2,
  SvgArrowLeft1Regular,
  Alert,
} from '@insights-ltd/design-library/components';
import { passwordValidationRules } from '@insights-ltd/design-library/utils';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import {
  FormControl,
  Button,
  Snackbar,
  Card,
  CardContent,
  styled,
  Link,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  ButtonBase,
  Theme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import ValidationTags from 'components/ValidationTags';
import { NO_SPECIAL_CHARACTERS_REGEX } from 'variables';
import { Trans, useTranslation } from 'react-i18next';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormWatch,
  useForm,
} from 'react-hook-form';
import { RequestError } from 'api';
import {
  isPreferredDialect,
  useGetApplicationPreferredDialects,
} from 'domain/dialect';
import SignUpLanguageSelection from 'components/SignUpLanguageSelection/SignUpLanguageSelection';
import { useBetaEnabled } from 'features';
import { Dialects, PreferredDialect } from 'types/dialects';
import { getPrivacyPolicyUrl } from 'utils/generalUtils';
import { FormData } from '../types';

const PolicyAcceptanceCheckbox = ({
  id,
  name,
  label,
  control,
  error,
  errorText,
}: {
  id: string;
  name: keyof FormData;
  label: React.ReactNode;
  control: Control<FormData>;
  error: boolean;
  errorText: string;
}) => {
  const errorLabelId = `${id}-error-label`;
  return (
    <FormControl
      sx={(theme) => ({
        width: '100%',
        margin: `${theme.spacing(spacingSizeMap.XS)} 0`,
      })}
      required
      error={error}
    >
      <FormControlLabel
        control={
          <Controller
            render={({ field: { onChange, onBlur, value } }) => (
              <Checkbox
                id={id}
                onBlur={onBlur}
                onChange={(e) => onChange(e.target.checked)}
                checked={Boolean(value)}
                name={name}
                color="primary"
                slotProps={{
                  input: {
                    'aria-describedby': errorLabelId,
                  },
                }}
              />
            )}
            name={name}
            control={control}
            rules={{ required: true }}
            defaultValue={false}
          />
        }
        label={label}
      />
      {error && <FormHelperText id={errorLabelId}>{errorText}</FormHelperText>}
    </FormControl>
  );
};

const TermsAndConditionsCheckboxLabel = ({
  preferredDialect = 'en-GB',
}: {
  preferredDialect: Dialects | undefined;
}) => {
  return (
    <Trans
      i18nKey="ui.event-management.sign-up.terms-conditions"
      components={{
        styledLink: (
          <Link
            href={getPrivacyPolicyUrl(preferredDialect)}
            target="_blank"
            rel="noopener"
          />
        ),
      }}
    />
  );
};

type SignUpFormProps = {
  onSubmit: (data: FormData) => void;
  isLoading?: boolean;
  signUpError?: RequestError | null;
  authenticationMethod: string;
};

const StyledH1 = styled(TextV2)(({ theme }) => ({
  marginBottom: `${theme.spacing(spacingSizeMap.XS)} !important`,
}));

const StyledTextSxProps = (theme: Theme) => ({
  width: '100%',
  '& > div': {
    width: '100%',
  },
  input: {
    width: '100% !important',
  },
  margin: `0 0 ${theme.spacing(spacingSizeMap.XS)} 0`,
});

const StyledIcon = styled(SvgArrowLeft1Regular)(({ theme }) => ({
  transform: 'scale(0.5)',
  stroke: theme.palette.primary.main,
  strokeWidth: '2',
}));

type PasswordInputProps = {
  watch: UseFormWatch<FormData>;
  control: Control<FormData>;
  register: UseFormRegister<FormData>;
  errors: FieldErrors<FormData>;
};

const PasswordInput = ({
  watch,
  control,
  register,
  errors,
}: PasswordInputProps) => {
  const { t } = useTranslation();
  const [showPassword] = watch(['showPassword']);
  const { ref: passwordRef, ...passwordFormProps } = register(
    'password',
    passwordValidationRules,
  );

  return (
    <>
      <Grid
        sx={(theme) => ({
          '> p': {
            marginBottom: theme.spacing(spacingSizeMap.S),
          },
        })}
        size={12}
      >
        <TextV2 variant="body1" color="textSecondary">
          {t('ui.event-management.authentication.password.instruction')}
        </TextV2>
        <ValidationTags
          sx={{
            marginBottom: '0rem',
            maxWidth: `${0.86 * 500}px`,
          }}
          value={watch('password') || ''}
          errors={errors.password}
        />
        <FormControl
          variant="outlined"
          sx={(theme) => ({
            width: '100%',
            margin: `${theme.spacing(spacingSizeMap.XS)} 0 0`,
            '> div': {
              marginBottom: '0',
            },
          })}
        >
          <TextInputV2
            data-testid="password-input"
            id="password"
            autoComplete="off"
            labelText={t('ui.event-management.sign-up.password.input-label')}
            placeholder={t(
              'ui.event-management.sign-up.password.input-placeholder',
            )}
            type={showPassword ? 'text' : 'password'}
            innerRef={passwordRef}
            {...passwordFormProps}
            error={Boolean(errors.password)}
            sx={StyledTextSxProps}
          />
        </FormControl>
      </Grid>
      <Grid
        sx={(theme) => ({
          paddingLeft: theme.spacing(0.5),
        })}
      >
        <FormControlLabel
          sx={(theme) => ({
            '& span': {
              fontWeight: theme.typography.fontWeightBold,
            },
          })}
          control={
            <Controller
              render={({ field: { onChange, onBlur, value } }) => (
                <Checkbox
                  id="show-password"
                  onBlur={onBlur}
                  onChange={(e) => onChange(e.target.checked)}
                  checked={Boolean(value)}
                  name="show-password"
                  color="primary"
                  slotProps={{
                    input: {
                      'aria-describedby': 'show-password',
                    },
                  }}
                />
              )}
              name="showPassword"
              control={control}
              defaultValue={false}
            />
          }
          label={t('ui.event-management.sign-up.show-password.input-label')}
        />
      </Grid>
    </>
  );
};

const SignUpForm = ({
  onSubmit,
  isLoading,
  signUpError,
  authenticationMethod,
}: SignUpFormProps) => {
  const { t, i18n } = useTranslation();
  const isBetaEnabled = useBetaEnabled('supported-languages');
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
    resetField,
    reset,
  } = useForm<FormData>({
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const [preferredDialect] = watch(['preferredDialect']);

  const handleSetPreferredDialect = (dialect?: PreferredDialect) => {
    if (!dialect && resetField) {
      resetField('preferredDialect');
      i18n.changeLanguage('en-GB');
      if (reset) {
        reset();
      }
    } else if (setValue) {
      if (isPreferredDialect(dialect, isBetaEnabled)) {
        i18n.changeLanguage(dialect);
      } else {
        i18n.changeLanguage('en-GB');
      }
      setValue('preferredDialect', dialect);
    }
  };

  const genericErrors: Record<number, string> = {
    404: 'ui.event-management.authentication.errors.token',
  };

  const dialectOptions = useGetApplicationPreferredDialects(isBetaEnabled);
  const { ref: fullNameRef, ...fullNameProps } = {
    ...register('fullName', {
      required: true,
      pattern: NO_SPECIAL_CHARACTERS_REGEX,
    }),
  };

  const { ref: preferredDialectRef, ...preferredDialectProps } = {
    ...register('preferredDialect', {
      required: true,
    }),
  };

  const specificErrors: Record<string, string> = {
    PASSWORD_INVALID:
      'ui.event-management.authentication.errors.invalid-password',
    INVITE_EXPIRED_OR_USED: 'ui.event-management.authentication.errors.token',
  };

  const fullNameHelperText =
    errors.fullName?.type === 'required'
      ? t('ui.event-management.sign-up.full-name.error.required')
      : errors.fullName?.type === 'pattern' &&
        t(
          'ui.event-management.my-account.edit.error.value-full-name-no-special-characters',
        );

  const isUnsupportedPreferredDialect = !isPreferredDialect(preferredDialect);

  return !preferredDialect ? (
    <SignUpLanguageSelection
      onSelect={(dialect: PreferredDialect) => {
        handleSetPreferredDialect(dialect);
      }}
    />
  ) : (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card
        sx={(theme) => ({
          maxWidth: '500px',
          padding: `${theme.spacing(spacingSizeMap.L)} ${theme.spacing(
            spacingSizeMap.L,
          )} 0`,
        })}
      >
        <CardContent sx={{ padding: '0' }}>
          <Grid
            spacing={1}
            container
            sx={{
              flexDirection: 'column',
              justifyContent: 'space-between',
              mt: -0.5,
              mb: -0.5,
            }}
          >
            <Grid
              sx={(theme) => ({
                paddingTop: '0 !important',
                paddingLeft: 0,
                marginLeft: '-2px',
                marginBottom: theme.spacing(spacingSizeMap.XS),
              })}
              size={12}
            >
              <ButtonBase
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
                onClick={() => {
                  handleSetPreferredDialect();
                }}
              >
                <StyledIcon />
                <TextV2 component="span" color="primary" variant="bodyBold">
                  {t('ui.event-management.sign-up.back')}
                </TextV2>
              </ButtonBase>
            </Grid>
            <Grid
              sx={(theme) => ({
                width: '100%',
                margin: `0 0 ${theme.spacing(spacingSizeMap.XS)}`,
                paddingLeft: theme.spacing(0.5),
              })}
              size={12}
            >
              <StyledH1 variant="h3">
                {t('ui.event-management.sign-up.heading')}
              </StyledH1>
              <TextV2 component="p" variant="body1">
                {t('ui.event-management.sign-up.instruction.beta')}
              </TextV2>
            </Grid>
            <Grid
              sx={(theme) => ({
                marginBottom: theme.spacing(spacingSizeMap.XS),
              })}
              size={12}
            >
              <Select
                id="preferredDialect"
                innerRef={preferredDialectRef}
                label={t(
                  'ui.event-management.sign-up.preferred-language.input-label',
                )}
                type="text"
                variant="outlined"
                helperText={t(
                  isUnsupportedPreferredDialect
                    ? 'ui.event-management.my-account.edit.preferred-language.supported-language'
                    : 'ui.event-management.sign-up.preferred-language.helper-text',
                )}
                sx={{
                  width: '100%',
                  '& > div': {
                    width: '100%',
                  },
                  input: {
                    width: '100% !important',
                  },
                  fieldset: {
                    borderColor: 'transparent !important',
                  },
                }}
                {...preferredDialectProps}
                disabled
                items={dialectOptions}
                value={watch('preferredDialect')}
              />
            </Grid>
            <Grid size={12}>
              <TextInputV2
                id="fullName"
                data-testid="full-name-input"
                labelText={t(
                  'ui.event-management.sign-up.full-name.input-label',
                )}
                placeholder={t(
                  'ui.event-management.sign-up.full-name.input-placeholder',
                )}
                sx={StyledTextSxProps}
                type="text"
                variant="outlined"
                error={Boolean(errors.fullName)}
                helperText={fullNameHelperText}
                innerRef={fullNameRef}
                autoComplete="off"
                {...fullNameProps}
              />
            </Grid>
            {authenticationMethod === 'PASSWORD' && (
              <PasswordInput
                watch={watch}
                control={control}
                register={register}
                errors={errors}
              />
            )}
            <Grid
              sx={(theme) => ({
                '& span': {
                  fontWeight: theme.typography.fontWeightBold,
                },
                '> div': {
                  display: 'flex',
                  alignItems: 'flex-start',
                },
                paddingLeft: theme.spacing(0.5),
              })}
            >
              <PolicyAcceptanceCheckbox
                id="accept-terms"
                name="acceptTerms"
                label={
                  <TermsAndConditionsCheckboxLabel
                    preferredDialect={preferredDialect}
                  />
                }
                control={control}
                error={Boolean(errors.acceptTerms)}
                errorText={t(
                  'ui.event-management.sign-up.terms-conditions.error.required',
                )}
              />
            </Grid>
            <Grid
              container
              sx={{
                justifyContent: 'flex-end',
                mt: -0.5,
                mr: -0.5,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                type="submit"
                size="large"
                disabled={isLoading}
              >
                {t('ui.event-management.sign-up.cta')}
              </Button>
            </Grid>
          </Grid>
          <Snackbar
            open={signUpError !== null}
            autoHideDuration={6000}
            sx={{ flexDirection: 'column' }}
          >
            {signUpError ? (
              <div>
                {signUpError
                  .getRequestErrorKeys(
                    'ui.event-management.authentication.errors.general',
                    genericErrors,
                    specificErrors,
                  )
                  .map((key) => (
                    <Alert
                      key={key}
                      elevation={6}
                      variant="filled"
                      severity="error"
                    >
                      {t(key)}
                    </Alert>
                  ))}
              </div>
            ) : undefined}
          </Snackbar>
        </CardContent>
      </Card>
    </form>
  );
};

export default SignUpForm;
